import React, { useContext, useState } from "react";
import { Row, Tooltip, formatNumber } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { groupAccountsByOrganization } from "byzantine";
import Account from "byzantine/src/Account";
import { groupAccounts } from "byzantine/src/AccountUtils";
import Filters from "byzantine/src/filters";
import { NotificationContext, Options } from "cerulean";
import cc from "classcat";
import PropTypes from "prop-types";
import EmptyAccountBalances from "./EmptyAccountBalances/EmptyAccountBalances";
import SectionCard from "../SectionCard";
import AccountContext from "../contexts/AccountContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import BalancesPlusDropdown from "./BalancesPlusDropdown";

const SortPopoverItem = ({
  children,
  isSelected = false,
  hasGutter = false,
  onClick = () => {},
  closeDropdown = () => {},
}) => {
  const handleKeyUp = ({ key }) => {
    if (key === "Enter") {
      onClick();
      closeDropdown();
    }
  };
  return (
    <button
      className={cc([
        "sortPopover-item",
        "button--reset hoverable clickable",
        "padding--y--xs padding--x--s",
        "fontSize--s",
      ])}
      onClick={onClick}
      onKeyUp={handleKeyUp}
      /**
       * The `menuitemradio` role communicates that only one item in a set
       * may be selected at a time. The set is defined by the nearest parent
       * with a role of `menu`.
       */
      role="menuitemradio"
      aria-checked={isSelected}
    >
      <Row gapSize="none" alignItems="center">
        {isSelected && (
          <Row.Item shrink>
            <div
              className={cc([
                "sortPopover-item-check",
                "alignChild--center--center",
                "fontColor--theme--primary fontWeight--semibold fontSize--l",
              ])}
            >
              <span className="narmi-icon-check" />
            </div>
          </Row.Item>
        )}
        <Row.Item>
          <span
            className={cc([
              {
                "padding--left--l": hasGutter && !isSelected,
              },
            ])}
          >
            <span className="padding--left--xxs">{children}</span>
          </span>
        </Row.Item>
      </Row>
    </button>
  );
};

SortPopoverItem.propTypes = {
  children: PropTypes.node.isRequired,
  isSelected: PropTypes.bool,
  hasGutter: PropTypes.bool,
  onClick: PropTypes.func,
  closeDropdown: PropTypes.func,
};

const GroupHeader = ({
  collapsible = false,
  isShowBalanceAndSorting = true,
  isCollapsed,
  setIsCollapsed = () => {},
  groupName,
  groupNameInfoText,
  balance = "",
}) => {
  const {
    getAccountSortingForGroup,
    clearSorting,
    sortGroupAlphabetically,
    sortGroupByBalance,
    sortGroupByAccountNumber,
    sortAscending,
    sortDescending,
  } = useContext(AccountContext);
  const accountSorting = getAccountSortingForGroup(groupName);
  const anyItemSelected = !!accountSorting?.sort_by;

  const PopoverContent = () => (
    <>
      <div role="menu" aria-label="Sort by">
        <div className="option-title fieldset-label">
          {anyItemSelected && <div style={{ width: "28px" }} />}
          SORT BY
        </div>
        <SortPopoverItem
          hasGutter={anyItemSelected}
          isSelected={accountSorting.sort_by === "alphabetically"}
          onClick={() =>
            accountSorting.sort_by === "alphabetically"
              ? clearSorting(groupName)
              : sortGroupAlphabetically(groupName, accountSorting.order)
          }
        >
          Sort alphabetically
        </SortPopoverItem>
        <SortPopoverItem
          hasGutter={anyItemSelected}
          isSelected={accountSorting.sort_by === "by_balance"}
          onClick={() =>
            accountSorting.sort_by === "by_balance"
              ? clearSorting(groupName)
              : sortGroupByBalance(groupName, accountSorting.order)
          }
        >
          Sort by balance
        </SortPopoverItem>
        <SortPopoverItem
          hasGutter={anyItemSelected}
          isSelected={accountSorting.sort_by === "by_account_number"}
          onClick={() =>
            accountSorting.sort_by === "by_account_number"
              ? clearSorting(groupName)
              : sortGroupByAccountNumber(groupName, accountSorting.order)
          }
        >
          Sort by account number
        </SortPopoverItem>
      </div>
      <hr
        style={{
          color: "var(--color-lightGrey)",
          width: "calc(100% - 16px)",
          marginTop: "10px",
        }}
      />
      <div role="menu" aria-label="Order">
        <div className="option-title fieldset-label">
          {anyItemSelected && <div style={{ width: "28px" }} />}
          ORDER
        </div>
        <SortPopoverItem
          hasGutter={anyItemSelected}
          isSelected={accountSorting.order === "ascending"}
          onClick={() => sortAscending(groupName, accountSorting.sort_by)}
        >
          Ascending
        </SortPopoverItem>
        <SortPopoverItem
          hasGutter={anyItemSelected}
          isSelected={accountSorting.order === "descending"}
          onClick={() => sortDescending(groupName, accountSorting.sort_by)}
        >
          Descending
        </SortPopoverItem>
      </div>
    </>
  );
  return (
    <div
      className="account-group-header"
      role="button"
      tabIndex={0}
      onClick={() => {
        setIsCollapsed(!isCollapsed);
      }}
      onKeyUp={({ key }) => {
        if (key === "Enter") setIsCollapsed(!isCollapsed);
      }}
    >
      <div className="fontWeight--bold padding--y--xs">
        <Row>
          <Row.Item>
            <div className="group-name">
              <div>{groupName}&nbsp;</div>
              {groupNameInfoText && (
                <Tooltip text={groupNameInfoText}>
                  <span className="narmi-icon-info fontColor--primary fontSize--xxs" />
                </Tooltip>
              )}
              {collapsible && (
                <span
                  className={`clickable narmi-icon-chevron-${
                    isCollapsed ? "down" : "up"
                  }`}
                />
              )}
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div className="balance-options">
              {isShowBalanceAndSorting && (
                <span
                  className={cc([
                    "fontColor--heading",
                    {
                      "margin--right--xxs": !isCollapsed,
                      "margin--right--l": isCollapsed,
                    },
                  ])}
                >
                  {balance}
                </span>
              )}
              {!isCollapsed && isShowBalanceAndSorting && (
                <div className="margin--right--xxs">
                  <Options className="popover-option">
                    <div style={{ minWidth: "max-content" }}>
                      <PopoverContent />
                    </div>
                  </Options>
                </div>
              )}
            </div>
          </Row.Item>
        </Row>
      </div>
    </div>
  );
};
GroupHeader.propTypes = {
  collapsible: PropTypes.bool,
  isShowBalanceAndSorting: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  setIsCollapsed: PropTypes.func,
  groupName: PropTypes.string,
  groupNameInfoText: PropTypes.string,
  balance: PropTypes.string,
};

const AccountRow = ({ account }) => {
  const { l10n } = useLocalization();
  const { updateAccount } = useContext(AccountContext);
  const { sendNotification } = useContext(NotificationContext);

  const accountName = (() => {
    if (account.isExternal()) {
      return account.getName();
    }
    if (!account.isInternalLoan()) {
      return account.getShortDescription();
    }

    return l10n.getString(
      "dashboard-account-description",
      {
        accountName: account.getName(),
        accountNumber: account.number || "",
      },
      account.getShortDescription()
    );
  })();

  return (
    <div
      className="padding--y--m brand-hover account-row"
      role="button"
      tabIndex={0}
      onKeyUp={({ key }) => {
        if (key === "Enter") window.location.assign(`/accounts/${account.id}`);
      }}
      onClick={() => window.location.assign(`/accounts/${account.id}`)}
    >
      <Row alignItems="center">
        <Row.Item>
          <span>
            <div>{accountName}</div>
            {account.getGroupName() === "CDs" &&
              !account.hidden &&
              account.metadata?.maturity_date?.value && (
                <div className="fontSize--s fontColor--secondary">
                  {l10n.getString("dashboard-account-cd-maturity-date", {
                    date: account.formattedData({
                      date: Filters.longMonthDayYear,
                    })?.maturity_date?.value,
                  })}
                </div>
              )}
          </span>
        </Row.Item>
        <Row.Item shrink>
          <div className="balance-options">
            <span>
              <span className="margin--right--xxs fontColor--primary">
                {account.displayBalance()}
              </span>
            </span>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className="margin--right--xxs"
              onKeyUp={(e) => {
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Options className="account-options">
                <Options.Option
                  icon="narmi-icon-star"
                  text={account.favorited ? "Unfavorite" : "Favorite"}
                  onClick={() => {
                    account
                      .toggleFavorited()
                      .then((response) =>
                        updateAccount(Account.deserialize(response.account))
                      )
                      .catch(() =>
                        sendNotification({
                          type: "negative",
                          text: l10n.getString("error-favorited-status"),
                        })
                      );
                  }}
                />
                <Options.Option
                  icon="narmi-icon-eye-off"
                  text={
                    account.hidden
                      ? l10n.getString("action-unhide-account")
                      : l10n.getString("action-hide-account-from-view")
                  }
                  onClick={() => {
                    account
                      .toggleHidden()
                      .then((response) =>
                        updateAccount(Account.deserialize(response.account))
                      )
                      .catch(() =>
                        sendNotification({
                          type: "negative",
                          text: l10n.getString("error-hidden-status"),
                        })
                      );
                  }}
                />
              </Options>
            </div>
          </div>
        </Row.Item>
      </Row>
    </div>
  );
};
AccountRow.propTypes = {
  account: PropTypes.instanceOf(Account),
};

const AccountGroup = ({
  collapsible = false,
  isShowBalanceAndSorting = true,
  groupName,
  groupNameInfoText,
  accountsForGroup = [],
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsible);
  if (accountsForGroup.length === 0) return null;
  return (
    <>
      <GroupHeader
        collapsible={collapsible}
        isShowBalanceAndSorting={isShowBalanceAndSorting}
        isCollapsed={isCollapsed}
        setIsCollapsed={collapsible ? setIsCollapsed : () => {}}
        groupName={groupName}
        groupNameInfoText={groupNameInfoText}
        balance={formatNumber(Account.sumBalancesForAccounts(accountsForGroup))}
      />
      {(!collapsible || !isCollapsed) && (
        <div className="account-rows">
          {accountsForGroup.map((account) => (
            <AccountRow key={account.id} account={account} />
          ))}
        </div>
      )}
    </>
  );
};
AccountGroup.propTypes = {
  collapsible: PropTypes.bool,
  isShowBalanceAndSorting: PropTypes.bool,
  groupName: PropTypes.string,
  groupNameInfoText: PropTypes.string,
  accountsForGroup: PropTypes.arrayOf(PropTypes.instanceOf(Account)),
};

const AccountBalances = () => {
  const { l10n } = useLocalization();
  const {
    accounts,
    favoritedAccounts,
    unfavoritedAccounts,
    hiddenAccounts,
    isUpdatingAccounts,
  } = useContext(AccountContext);
  const { multientity_aggregation } = useUserFeatures();
  const { currentUser } = useCurrentUser();
  const only_credit_favorited_accounts = favoritedAccounts.filter(
    (account) => account.account_type === "credit"
  ).length;
  const only_deposit_favorited_accounts = favoritedAccounts.filter(
    (account) => account.account_type === "deposit"
  ).length;
  const shouldGroupByOrg =
    groupAccountsByOrganization(accounts, currentUser?.org_name).size > 1 &&
    multientity_aggregation;
  const groupedUnfavoritedAccounts = groupAccountsByOrganization(
    unfavoritedAccounts,
    currentUser?.org_name
  );
  const displayGroupNameInfoText = () => {
    if (
      only_credit_favorited_accounts === favoritedAccounts.length ||
      only_deposit_favorited_accounts === favoritedAccounts.length
    ) {
      return false;
    } else {
      return true;
    }
  };

  const groupNameInfoText = displayGroupNameInfoText()
    ? l10n.getString("tooltip-favorites-balance")
    : "";

  const translateGroupName = (groupName) => {
    const groupTranslationMap = {
      Favorites: l10n.getString("account-group-favorites"),
      Checking: l10n.getString("account-group-checking"),
      Savings: l10n.getString("account-group-savings"),
      "Credit cards": l10n.getString("account-group-credit-cards"),
      Loans: l10n.getString("account-group-loans"),
      CDs: l10n.getString("account-group-cds"),
      Others: l10n.getString("account-group-others"),
      "External accounts": l10n.getString("account-group-external-accounts"),
      "Hidden accounts": l10n.getString("hidden-accounts"),
    };
    return groupTranslationMap[groupName] || groupName;
  };

  return (
    <SectionCard isLoading={isUpdatingAccounts}>
      {accounts.length === 0 ? (
        <EmptyAccountBalances />
      ) : (
        <>
          <Row
            alignItems="center"
            gapSize="none"
            className="padding--bottom--m"
          >
            <Row.Item grow>
              <SectionCard.Title
                text={l10n.getString("heading-balances", null, "Balances")}
                testId="balances-heading"
              />
            </Row.Item>
            <BalancesPlusDropdown />
          </Row>
          <div>
            <AccountGroup
              groupName={translateGroupName("Favorites")}
              accountsForGroup={favoritedAccounts}
              groupNameInfoText={groupNameInfoText}
            />
            {shouldGroupByOrg
              ? [...groupedUnfavoritedAccounts].map(
                  ([orgName, accountsByOrg]) => (
                    <AccountGroup
                      groupName={orgName}
                      accountsForGroup={accountsByOrg}
                      key={orgName}
                    />
                  )
                )
              : Array.from(
                  groupAccounts(unfavoritedAccounts),
                  ([groupName, accountsForGroup]) => (
                    <AccountGroup
                      collapsible={groupName === "CDs"}
                      groupName={translateGroupName(groupName)}
                      key={groupName}
                      accountsForGroup={accountsForGroup}
                    />
                  )
                )}
            <AccountGroup
              collapsible
              isShowBalanceAndSorting={false}
              groupName={translateGroupName("Hidden accounts")}
              accountsForGroup={hiddenAccounts}
            />
          </div>
        </>
      )}
    </SectionCard>
  );
};
AccountBalances.propTypes = {};

export default AccountBalances;
